import { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { t, subscribeLanguage, AVAILABLE_LANGUAGES } from '../i18n';
import { getSettings, setSettings } from '../game/systems/settings';

export default function SettingsScreen({ onBack }) {
  const [s, setS] = useState(getSettings());
  useEffect(() => subscribeLanguage(() => setS({ ...getSettings() })), []);

  const update = (patch) => {
    setSettings(patch);
    setS({ ...getSettings() });
  };

  return (
    <div data-testid="settings-screen" className="neon-screen">
      <div className="neon-ocean-bg neon-ocean-bg-dim" />
      <div className="neon-settings-content">
        <button data-testid="btn-back" onClick={onBack} className="neon-btn neon-btn-ghost neon-btn-back">
          <ArrowLeft size={18} />
          <span>{t('common.back')}</span>
        </button>

        <h2 className="neon-settings-title">{t('settings.title')}</h2>

        <div className="neon-settings-list">
          <Row label={t('settings.musicVolume')}>
            <input
              data-testid="setting-music"
              type="range" min="0" max="1" step="0.05"
              value={s.musicVolume}
              onChange={(e) => update({ musicVolume: parseFloat(e.target.value) })}
              className="neon-range"
            />
            <span className="neon-range-value">{Math.round(s.musicVolume * 100)}</span>
          </Row>

          <Row label={t('settings.effectsVolume')}>
            <input
              data-testid="setting-effects"
              type="range" min="0" max="1" step="0.05"
              value={s.effectsVolume}
              onChange={(e) => update({ effectsVolume: parseFloat(e.target.value) })}
              className="neon-range"
            />
            <span className="neon-range-value">{Math.round(s.effectsVolume * 100)}</span>
          </Row>

          <Row label={t('settings.vibration')}>
            <Toggle
              testid="setting-vibration"
              value={s.vibration}
              onChange={(v) => update({ vibration: v })}
              on={t('settings.on')} off={t('settings.off')}
            />
          </Row>

          <Row label={t('settings.graphicsQuality')}>
            <SegmentedControl
              testid="setting-quality"
              value={s.graphicsQuality}
              onChange={(v) => update({ graphicsQuality: v })}
              options={[
                { value: 'low', label: t('settings.low') },
                { value: 'medium', label: t('settings.medium') },
                { value: 'high', label: t('settings.high') },
              ]}
            />
          </Row>

          <Row label={t('settings.touchControls')}>
            <SegmentedControl
              testid="setting-touch"
              value={s.touchControls}
              onChange={(v) => update({ touchControls: v })}
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'on', label: t('settings.on') },
                { value: 'off', label: t('settings.off') },
              ]}
            />
          </Row>

          <Row label={t('settings.language')}>
            <SegmentedControl
              testid="setting-lang"
              value={s.language}
              onChange={(v) => update({ language: v })}
              options={AVAILABLE_LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))}
            />
          </Row>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="neon-setting-row">
      <label className="neon-setting-label">{label}</label>
      <div className="neon-setting-control">{children}</div>
    </div>
  );
}

function Toggle({ value, onChange, on, off, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!value)}
      className={`neon-toggle ${value ? 'neon-toggle-on' : ''}`}
    >
      <span className="neon-toggle-dot" />
      <span className="neon-toggle-label">{value ? on : off}</span>
    </button>
  );
}

function SegmentedControl({ value, onChange, options, testid }) {
  return (
    <div data-testid={testid} className="neon-segmented">
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`${testid}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`neon-segment ${value === o.value ? 'neon-segment-active' : ''}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
